import React, { Fragment, Component } from 'react';
import Translate from 'react-translate-component';
import { Field, FormSpy } from 'react-final-form';
import { any, propEq, path } from 'ramda';
import { withAsyncActions } from 'react-async-client';
import moment from 'moment';
import cx from 'classnames';

import { DATE_FORMATS } from 'constants/dateFormats';
import { budget, reason } from 'constants/dictionaries';
import { isReplacing } from 'helpers/dictionariesHelpers';
import { getTowns, getOrganizations, getLegalDepartmentCells } from 'actions/asyncActions';
import ListenerField from 'components/common/form/ListenerField';
import CustomIcon from 'components/common/CustomIcon';
import { RadioButtonsLoad } from 'components/common/form/RadioButtons';
import { NATURAL_NUMBER } from 'constants/regexp';
import Select from 'components/common/form/Select';
import SearchInput from 'components/common/form/SearchInput';
import Text from 'components/common/form/Text';
import Input from 'components/common/form/Input';
import InputNumberMask from 'components/common/form/InputNumberMask';
import DatePicker from 'components/common/form/DatePicker';
import ReplacingField from './ReplacingField';
import DepartmentField from './DepartmentField';
import LegalFields from './LegalFields';
import FullLegalFields from './FullLegalFields';
import { maxLength, required, composeValidators } from 'utils/validators';

const isPast = (date) => date ? moment(date, [DATE_FORMATS.format]).isBefore(moment(), 'day') : false;

@withAsyncActions({
    getTowns,
    getLegalDepartmentCells,
    getOrganizations: getOrganizations
        .withOptions({ dispatchOnMount: true, autoReset: true })
        .withSuccessHandler(({ isLegal, user: { company }, getOrganizations: { data }, form: { change } }) => {
            if (!isLegal && any(propEq('id', company), data)) {
                change('organization', company.toString());
            }
        })
})
export default class BasicInformationForm extends Component {
    onSearch = (value) => {
        if (value) {
            this.props.getTowns.dispatch({ value });
        }
    }

    render () {
        const {
            isLegal = false,
            isFullLegal = false,
            hasType,
            isNewType,
            user,
            legal,
            getLegalDepartmentCells: { data : cells },
            getOrganizations: { data: organizations }, getOrganizations,
            getTowns: { data: towns, meta: { pending: townsPending }}
        } = this.props;

        return (
            <div className="card">
                <div className="card-head">
                    <h3>
                        <CustomIcon type="info-outline"/>
                        <Translate content='request.headers.basicInformation' component='span' />
                    </h3>
                </div>
                <div className="card-body">
                    { isLegal ? (
                        isFullLegal ? (
                            <FullLegalFields user={user} form={this.props.form} legal={legal} />
                        ) : (
                            <LegalFields user={user} form={this.props.form} />
                        )
                    ) : (
                        <Fragment>
                            <Field
                                name='organization'
                                component={Select}
                                options={organizations}
                                dictionaryAction={getOrganizations}
                                label={{ content: 'request.company', required: true }}
                                placeholder={<Translate content='placeholders.chooseCompany' />}
                                validate={required}
                            />
                            <ListenerField
                                listenFieldName='organization'
                            >
                                { (values) => (
                                    <DepartmentField organization={values.organization} />
                                )}
                            </ListenerField>
                        </Fragment>
                    )}
                    { !isFullLegal || hasType ? (
                        <Fragment>
                            <Field
                                name='position'
                                component={Input}
                                label={{ content: 'request.position', required: true }}
                                placeholder='placeholders.enterPosition'
                                validate={maxLength(127)}
                            />

                            <ListenerField
                                listenFieldName={['townPlaceholder']}
                            >
                                { ({ townPlaceholder }) =>
                                    <Field
                                        name='town'
                                        allowClear={true}
                                        component={SearchInput}
                                        onSearch={this.onSearch}
                                        options={towns}
                                        pending={townsPending}
                                        label={{ content: 'request.town' }}
                                        placeholderDefault
                                        placeholder={townPlaceholder || <Translate content='placeholders.enterTown' />}
                                    />
                                }
                            </ListenerField>

                            <FormSpy subscription={{ values: true }}>
                                { ({ values }) => {
                                    const cell = path(['kadrsyst', 'cell'], values);
                                    const item = (cells || []).find(item  => item.id === Number(cell));
                                    const showExplanation = isFullLegal && (isNewType ? true : item && (
                                        Number(path(['quantity'], values)) > Number(path(['numrates'], item))
                                    ));

                                    return (
                                        <Fragment>
                                            <Field
                                                name='quantity'
                                                component={InputNumberMask}
                                                mask={NATURAL_NUMBER}
                                                fieldClass={cx('quantity-field', showExplanation && !isNewType && 'quantity-field-legal-warning')}
                                                label={{ content: 'request.quantity', required: true }}
                                                placeholder='placeholders.quantity'
                                            />
                                            { showExplanation && (
                                                <Field name='explanation'
                                                    component={Text}
                                                    label={{ content: 'request.legalExplanation', required: true }}
                                                    placeholder='placeholders.enterExplanation'
                                                    validate={composeValidators(required, maxLength(1023))}
                                                />
                                            )}
                                        </Fragment>
                                    );
                                }}
                            </FormSpy>

                            <Field
                                name='deadline'
                                component={DatePicker}
                                label={{ content: 'request.deadline' }}
                                disabledDate={isPast}
                            />

                            <Field
                                name='budget'
                                component={RadioButtonsLoad}
                                loadName={budget}
                                namePath='value'
                                label={{ content: 'request.budget' }}
                            />

                            <Field
                                name='reason'
                                component={RadioButtonsLoad}
                                loadName={reason}
                                namePath='value'
                                label={{ content: 'request.reason' }}
                                onChange={value => {
                                    if (!isReplacing(value)) {
                                        this.props.form.change('replacing', null);
                                    }
                                }}
                            />

                            <ListenerField
                                listenFieldName={['reason', 'organization', 'department']}
                            >
                                { values => !isLegal && isReplacing(values.reason) && (
                                    <ReplacingField
                                        {...this.props}
                                        organizationId={values.organization}
                                        departmentId={values.department} />
                                )}
                            </ListenerField>
                        </Fragment>
                    ) : null}
                </div>
            </div>
        );
    }
}
